/**
 * Exporta linhas para um arquivo Excel (.xlsx) — genérico, para qualquer listagem.
 *
 * Existe porque os dois exportadores que o backoffice já tinha não serviam como base:
 * - `credit-analysis/shared/exportToExcel.ts` usa `xlsx`, mas formata os números como TEXTO
 *   ("R$ 1.500,50"): a planilha abre bonita e não soma, não ordena e não filtra por valor.
 * - `monitoring/ExportDropdown.tsx` busca todas as páginas, mas gera CSV.
 *
 * Aqui cada célula sai com o TIPO certo — número é número (com formato de moeda), data é data —,
 * então quem abre a planilha consegue somar a coluna de valor e filtrar por vencimento, que é
 * para isso que se exporta.
 *
 * Usa a `xlsx` que já é dependência do projeto: nenhuma lib nova.
 */
import * as XLSX from 'xlsx';

export type XlsxCellType = 'text' | 'currency' | 'date' | 'integer';

export interface XlsxColumn<T> {
    header: string;
    value: (row: T) => string | number | null | undefined;
    type?: XlsxCellType;
    /** largura em caracteres; sem ela, calcula pelo conteúdo */
    width?: number;
}

const CURRENCY_FORMAT = '"R$" #,##0.00';
const DATE_FORMAT = 'dd/mm/yyyy';

/**
 * `YYYY-MM-DD` como data LOCAL.
 *
 * ⚠️ `new Date('2026-10-01')` é interpretado como UTC pela spec, e no Brasil vira 30/09 às 21h — a
 * planilha mostraria o vencimento um dia antes. Mesmo cuidado do `parseLocalDate` da tela de
 * monitoramento (AF-3447).
 */
export const parseLocalDate = (raw: string | null | undefined): Date | null => {
    if (!raw) return null;
    const dateOnly = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
    if (dateOnly) {
        const [, y, m, d] = dateOnly;
        return new Date(Number(y), Number(m) - 1, Number(d));
    }
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/**
 * A célula já tipada. `null` vira célula VAZIA, e não "-": um traço numa coluna de valor faz o
 * Excel tratar a coluna inteira como texto e a soma dá erro.
 */
const toCell = (raw: unknown, type: XlsxCellType): XLSX.CellObject | null => {
    if (raw === null || raw === undefined || raw === '') return null;
    switch (type) {
        case 'currency': {
            const n = Number(raw);
            return Number.isFinite(n) ? { t: 'n', v: n, z: CURRENCY_FORMAT } : null;
        }
        case 'integer': {
            const n = Number(raw);
            return Number.isFinite(n) ? { t: 'n', v: n, z: '0' } : null;
        }
        case 'date': {
            const d = parseLocalDate(String(raw));
            return d ? { t: 'd', v: d, z: DATE_FORMAT } : null;
        }
        default:
            // Tipo `s` explícito: o texto é gravado como TEXTO, nunca como fórmula. Uma razão social
            // começando com "=" ou "+" não vira cálculo ao abrir — a injeção de fórmula que o
            // exportador de CSV do monitoramento precisa neutralizar à mão não acontece aqui.
            return { t: 's', v: String(raw) };
    }
};

/** Largura pelo conteúdo, com teto — sem teto, uma observação longa faria uma coluna de 300. */
const widthOf = <T,>(column: XlsxColumn<T>, rows: T[]): number => {
    if (column.width) return column.width;
    const longest = rows.reduce((max, row) => {
        const v = column.value(row);
        const len = v === null || v === undefined ? 0 : String(v).length;
        return Math.max(max, len);
    }, column.header.length);
    return Math.min(Math.max(longest + 2, 8), 60);
};

/** A planilha em memória — separada do download para poder ser testada. */
export const buildWorkbook = <T,>(rows: T[], columns: XlsxColumn<T>[], sheetName = 'Dados') => {
    const sheet: XLSX.WorkSheet = {};
    columns.forEach((column, c) => {
        sheet[XLSX.utils.encode_cell({ r: 0, c })] = { t: 's', v: column.header };
    });
    rows.forEach((row, r) => {
        columns.forEach((column, c) => {
            const cell = toCell(column.value(row), column.type ?? 'text');
            if (cell) sheet[XLSX.utils.encode_cell({ r: r + 1, c })] = cell;
        });
    });
    sheet['!ref'] = XLSX.utils.encode_range({
        s: { r: 0, c: 0 },
        e: { r: rows.length, c: Math.max(columns.length - 1, 0) },
    });
    sheet['!cols'] = columns.map(column => ({ wch: widthOf(column, rows) }));
    // cabeçalho congelado e com autofiltro: é a primeira coisa que alguém faz ao abrir
    sheet['!autofilter'] = { ref: sheet['!ref'] };

    const workbook = XLSX.utils.book_new();
    // o Excel recusa nome de aba com mais de 31 caracteres ou com : \ / ? * [ ]
    XLSX.utils.book_append_sheet(workbook, sheet, sheetName.replace(/[:\\/?*[\]]/g, ' ').slice(0, 31));
    return workbook;
};

export const exportToXlsx = <T,>(rows: T[], columns: XlsxColumn<T>[], filename: string,
                                  sheetName?: string): void => {
    const workbook = buildWorkbook(rows, columns, sheetName);
    XLSX.writeFile(workbook, filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`,
                   { cellDates: true });
};

/**
 * TODAS as páginas de uma listagem paginada, na ordem.
 *
 * O laço vem do exportador do monitoramento; a diferença é que para por `totalPages` e não por
 * "página veio incompleta" — este último pararia cedo se o servidor devolvesse menos linhas numa
 * página por qualquer motivo, e a planilha sairia faltando sem avisar.
 *
 * `onProgress` existe porque uma exportação grande leva segundos: sem retorno visual, quem clicou
 * clica de novo.
 */
export const fetchAllPages = async <T,>(
    fetchPage: (page: number) => Promise<{ list?: T[]; totalPages?: number }>,
    onProgress?: (loaded: number, totalPages: number) => void,
): Promise<T[]> => {
    const all: T[] = [];
    let page = 1;
    let totalPages = 1;
    do {
        const result = await fetchPage(page);
        all.push(...(result.list ?? []));
        totalPages = result.totalPages ?? 1;
        onProgress?.(page, totalPages);
        page += 1;
    } while (page <= totalPages);
    return all;
};
